import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"

interface DropdownMenuContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLElement>
  openedAtRef: React.MutableRefObject<number>
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | undefined>(undefined)

interface DropdownMenuProps {
  children: React.ReactNode
}

const DropdownMenu = ({ children }: DropdownMenuProps) => {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLElement>(null)
  const openedAtRef = React.useRef<number>(0)

  const setOpenWithTimestamp = React.useCallback((newOpen: boolean) => {
    if (newOpen) {
      openedAtRef.current = Date.now()
    }
    setOpen(newOpen)
  }, [])

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen: setOpenWithTimestamp, triggerRef, openedAtRef }}>
      <div className="relative">{children}</div>
    </DropdownMenuContext.Provider>
  )
}

interface DropdownMenuTriggerProps {
  asChild?: boolean
  children: React.ReactElement
}

const DropdownMenuTrigger = ({ asChild, children }: DropdownMenuTriggerProps) => {
  const context = React.useContext(DropdownMenuContext)
  if (!context) throw new Error("DropdownMenuTrigger must be used within DropdownMenu")

  const handleClick = (e: React.MouseEvent) => {
    console.log('[DropdownMenuTrigger] Clicked! Current open state:', context.open)
    e.preventDefault()
    e.stopPropagation()
    context.setOpen(!context.open)
    console.log('[DropdownMenuTrigger] New open state:', !context.open)
    if (children.props.onClick) {
      children.props.onClick(e)
    }
  }

  if (asChild) {
    return React.cloneElement(children, {
      ref: context.triggerRef,
      onClick: handleClick,
    })
  }

  return (
    <button ref={context.triggerRef as React.RefObject<HTMLButtonElement>} onClick={handleClick}>
      {children}
    </button>
  )
}

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'center' | 'end'
  children: React.ReactNode
}

const DropdownMenuContent = ({ className, align = 'start', children, ...props }: DropdownMenuContentProps) => {
  const context = React.useContext(DropdownMenuContext)
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null)
  const [canClose, setCanClose] = React.useState(false)

  console.log('[DropdownMenuContent] Render - open:', context?.open, 'position:', position)

  if (!context) throw new Error("DropdownMenuContent must be used within DropdownMenu")

  React.useEffect(() => {
    if (context.open) {
      setCanClose(false)
      const timer = setTimeout(() => {
        setCanClose(true)
      }, 100)
      return () => clearTimeout(timer)
    } else {
      setPosition(null)
    }
  }, [context.open])

  React.useEffect(() => {
    if (context.open && context.triggerRef.current) {
      const rect = context.triggerRef.current.getBoundingClientRect()
      let left = rect.left

      if (align === 'center') {
        left = rect.left + rect.width / 2
      } else if (align === 'end') {
        left = rect.right
      }

      const newPosition = {
        top: rect.bottom + 8,
        left: left,
      }
      setPosition(newPosition)
    }
  }, [context.open, align, context.triggerRef])

  if (!context.open || !position) {
    return null
  }

  console.log('[DropdownMenuContent] About to render children. Children type:', typeof children, 'Children:', children)

  // Log each child in detail
  if (Array.isArray(children)) {
    children.forEach((child, index) => {
      console.log(`[DropdownMenuContent] Child ${index}:`, child, 'Type:', child?.type, 'Props:', child?.props)
    })
  }

  const alignClass = {
    start: '',
    center: '-translate-x-1/2',
    end: '-translate-x-full',
  }

  const handleBackdropClick = () => {
    const timeSinceOpen = Date.now() - context.openedAtRef.current
    if (timeSinceOpen > 50 && canClose) {
      context.setOpen(false)
    }
  }

  console.log('[DropdownMenuContent] Creating portal now')

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[999] bg-black/20"
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleBackdropClick()
        }}
      />
      <div
        className={cn(
          "fixed z-[1000] min-w-[8rem] overflow-hidden rounded border border-zinc-800 bg-zinc-900 p-1 shadow-2xl pointer-events-auto",
          alignClass[align],
          className
        )}
        style={{ top: position.top, left: position.left }}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
      </div>
    </>,
    document.body
  )
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean
  children: React.ReactNode
}

const DropdownMenuItem = ({ className, asChild, children, onClick, ...props }: DropdownMenuItemProps) => {
  const context = React.useContext(DropdownMenuContext)
  if (!context) throw new Error("DropdownMenuItem must be used within DropdownMenu")

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(e)
    context.setOpen(false)
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement, {
      className: cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-zinc-800",
        className
      ),
      onClick: handleClick,
    })
  }

  return (
    <div
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-zinc-800",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  )
}

const DropdownMenuSeparator = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("-mx-1 my-1 h-px bg-zinc-800", className)} {...props} />
)

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
}
